/**
 * D331 — the operator's notice, mounted once alongside `<App/>` (`main.tsx`) rather than inside
 * `screens/home/`.
 *
 * **Why it lives here and is not simply JSX inside `HomeScreen.tsx`.** R9 §5's fail-open rule
 * refuses `web/src/config/` to anything the open-path import graph reaches, and
 * `infra/tests/client-config-client.test.ts` walks that graph from every file **directly inside**
 * `screens/home/` — `HomeScreen.tsx` is one of those roots, and so is everything it imports
 * transitively, including the whole of `components/`'s barrel. So this component cannot be
 * imported by `HomeScreen.tsx`, or exported from `components/index.ts` (which `HomeScreen.tsx`
 * already imports whole). It is exported from `config/index.ts` instead, imported only by
 * `main.tsx`, and rendered as a sibling of `<App/>` in the *same* React root — which is what makes
 * the portal below deliver real, working DOM events rather than an inert copy: `createPortal`
 * relocates only where a subtree paints, never which fiber tree owns its event delegation, exactly
 * as `components/FrameOverlay.tsx` already relies on for the shell's own overlays.
 *
 * **Where it paints.** `components/homeNoticeHost.ts` is a tiny, import-free registry:
 * `HomeScreen.tsx` registers a `<div>` into it (a callback `ref`, so mount hands over the node and
 * unmount hands over `null`) in the exact slot D275's downloaded-update banner occupies, and only
 * while that banner is not itself showing — the mutual-exclusion rule lives at that call site, in
 * `HomeScreen.tsx`, not here. This component only ever asks "is there a slot right now," and paints
 * nothing when the answer is no — covering both "Home is not the visible screen" and "D275's banner
 * has the slot."
 *
 * **What it composes**, each already built by an earlier R9 session: the resolved document's
 * `notice` (`store.ts`, D328), the dismissal record (`noticeDismissal.ts`, D331), and the
 * locale-keyed fallback (`noticeText.ts`, R9 §4.5 / `adding-a-locale.md` §6). No links, no markup
 * beyond the sentence itself, no action but the one dismiss control (R9 §4.5: "a notice that wants
 * a button is a surface, and a surface is a PRD"). The text renders exactly as the operator wrote
 * it — no bold headline, no "Announcement:" framing — because `web/AGENTS.md`'s rule that a message
 * may only assert what something actually did applies to operator copy too, and a bold app-styled
 * headline over a sentence the app did not write would dress it as one of the app's own claims.
 */
import { useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { getHomeNoticeHost, subscribeHomeNoticeHost } from '../components/homeNoticeHost'
import { Icon } from '../components/icons/Icon'
import { useTranslation } from '../i18n/I18nProvider'
import { dismissNotice, getDismissedNoticeId, subscribeToDismissedNotice } from './noticeDismissal'
import { resolveNoticeText } from './noticeText'
import { getClientConfig, subscribeToClientConfig } from './store'

export function HomeNoticePortal(): JSX.Element | null {
  const host = useSyncExternalStore(subscribeHomeNoticeHost, getHomeNoticeHost, getHomeNoticeHost)
  const notice = useSyncExternalStore(
    subscribeToClientConfig,
    () => getClientConfig().notice,
    () => getClientConfig().notice,
  )
  const dismissedId = useSyncExternalStore(
    subscribeToDismissedNotice,
    getDismissedNoticeId,
    getDismissedNoticeId,
  )
  const { t, locale } = useTranslation('home')

  if (host === null || notice === undefined || notice.id === dismissedId) return null
  const text = resolveNoticeText(notice.text, locale)
  if (text === undefined) return null

  /*
   * The container class is `.update-ready` itself, unmodified — D275's own geometry (margin,
   * padding, border, the neutral `--panel`/`--border` treatment that D228 requires: no semantic
   * colour, because this sentence is the operator's and not a claim the app is making about a gate
   * or a command). Only the inner text and the dismiss control are new selectors
   * (`global.css`, right below `.update-ready`'s own block).
   */
  return createPortal(
    <div className="update-ready" role="status">
      <Icon name="bell" size={15} />
      <div className="update-ready-text">
        <span className="home-notice-text">{text}</span>
      </div>
      <button
        type="button"
        className="home-notice-dismiss"
        aria-label={t('dismissNoticeLabel')}
        onClick={() => dismissNotice(notice.id)}
      >
        <Icon name="x" size={14} />
      </button>
    </div>,
    host,
  )
}
